// useVisitWorkspace.js — what the visit view can open, in the clinician's
// order, split into pinned (own tab) and unpinned (inside the Apps tab).
// Both details views (CX and Native) use it, and it publishes the same list to
// the Free Compliance rail so the rail's buttons match the tabs.
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from '../../api/GlobalContext';
import { dragState } from './WorkspaceEdit';
import useVendorStore from '../../utils/vendorApps/useVendorStore';
import {
  APPS_ID,
  LOCKED,
  connectAccount,
  idForTabKey,
  isVendorItem,
  loadLayout,
  moveItem,
  nudgeItem,
  publishActive,
  publishCatalog,
  resolveLayout,
  saveLayout,
  setPinned as setPinnedIn,
  subscribeLayout,
  tabKeyFor,
  vendorAppIdOf,
  vendorItemId,
} from '../../utils/workspace/workspaceLayout';

/** Built-ins: label and tile colour. Icons: utils/workspace/workspaceIcons.js, by id. */
export const BUILTINS = {
  note: { label: 'Note', color: '#5e72e4' },
  chat: { label: 'Ask', color: '#11cdef' },
  compliance: { label: 'Compliance', color: '#2dce89' },
  coding: { label: 'Coding', color: '#fb6340' },
};

/**
 * Pin or unpin `id` and put it next to `targetId` (null = the end of that
 * group): every move in edit mode, dragged or clicked. Pure, so a drag can
 * preview it before it is saved.
 */
function placeIn(layout, id, { pinned, targetId = null, after = false }, available) {
  let next = setPinnedIn(layout, id, pinned);
  const rest = () => next.order.filter((x) => x !== id);
  if (targetId && targetId !== id) {
    const order = rest();
    const at = order.indexOf(targetId);
    return moveItem(next, id, after ? order[at + 1] ?? null : targetId);
  }
  // No target: the end of that group (among what's on screen).
  const group = next.order.filter((x) => x !== id && available.has(x) && (LOCKED.includes(x) || next.pinned.includes(x)) === pinned);
  const last = group[group.length - 1];
  if (last) {
    const order = rest();
    next = moveItem(next, id, order[order.indexOf(last) + 1] ?? null);
  }
  return next;
}

/**
 * @param {object}   opts
 * @param {string[]} opts.builtins     built-in ids available on this visit, e.g. ['note','chat','compliance']
 * @param {boolean}  opts.vendorAppsOn include the group's connected apps
 * @param {string}   opts.activeTab    the view's Tabs activeKey
 * @param {Function} opts.setActiveTab
 */
export default function useVisitWorkspace({ builtins, vendorAppsOn, activeTab, setActiveTab, labels = {} }) {
  const store = useVendorStore();
  const [stored, setStored] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // The unpinned item open inside the Apps tab.
  const [appsSelection, setAppsSelection] = useState(null);
  // Edit layout ("jiggle") mode: only then do tabs and tiles drag.
  const [editing, setEditingState] = useState(false);
  // The right-click menu: { id, x, y } | null.
  const [menu, setMenu] = useState(null);
  // The tabs don't all fit at full width: inactive ones show their icon only.
  const [compact, setCompact] = useState(false);
  // What is being dragged in edit mode, so the tab bar can show it is a drop target.
  const [dragging, setDragging] = useState(null);

  const { apiClient } = useContext(GlobalContext) || {};
  useEffect(() => {
    let live = true;
    loadLayout().then((layout) => {
      if (!live) return;
      setStored(layout);
      setLoaded(true);
      // Then the account's copy, which wins unless there's an unsynced edit
      // here; a change arrives through subscribeLayout.
      connectAccount(apiClient);
    });
    const off = subscribeLayout((layout) => setStored(layout));
    return () => {
      live = false;
      off();
    };
  }, [apiClient]);

  const vendorApps = vendorAppsOn ? store.apps.filter((a) => a.enabled) : [];
  // Apps waiting on the clinician for this patient (a mismatch, a patient to
  // pull in, or no context yet): their tab, tile and rail button wear a dot.
  const needing = new Set(vendorAppsOn ? store.appsNeedingAttention().map((a) => a.id) : []);
  const vendorKey = vendorApps.map((a) => `${a.id}:${a.name}:${a.color}:${(a.icon || '').length}:${needing.has(a.id) ? 1 : 0}`).join('|');
  const labelKey = JSON.stringify(labels);

  const items = useMemo(() => {
    const list = builtins
      .filter((id) => BUILTINS[id])
      .map((id) => ({ id, kind: 'builtin', ...BUILTINS[id], label: labels[id] || BUILTINS[id].label }));
    vendorApps.forEach((app) =>
      list.push({ id: vendorItemId(app.id), kind: 'vendor', appId: app.id, label: app.name, icon: app.icon, color: app.color || '#5e72e4', description: app.description, attention: needing.has(app.id) }),
    );
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtins.join('|'), vendorKey, labelKey]);

  const byId = useMemo(() => Object.fromEntries(items.map((item) => [item.id, item])), [items]);
  const resolved = useMemo(() => resolveLayout(stored, items.map((i) => i.id)), [stored, items]);

  // A drag in edit mode previews where the item would land: the tabs and the
  // tray re-flow live, home-screen style, and the drop just saves what's on
  // screen. Everything except what's drawn (isPinned, open, the active tab)
  // keeps reading the saved layout, so a preview never switches tabs.
  const [preview, setPreviewState] = useState(null); // { id, pinned, targetId, after }
  const previewRef = useRef(null);
  const setPreviewBoth = (next) => {
    if (JSON.stringify(previewRef.current) === JSON.stringify(next)) return;
    previewRef.current = next;
    setPreviewState(next);
  };
  const view = useMemo(() => {
    if (!preview || !items.some((i) => i.id === preview.id)) return { pinned: resolved.pinned, unpinned: resolved.unpinned, ghost: null };
    const ids = items.map((i) => i.id);
    const next = resolveLayout(placeIn(resolved.layout, preview.id, preview, new Set(ids)), ids);
    // The item's old spot keeps a faint ghost until the drop: where it came
    // from, and the drag's source element staying put so the drag survives.
    const wasPinned = resolved.pinned.includes(preview.id);
    let pinned = next.pinned;
    let unpinned = next.unpinned;
    let ghost = null;
    if (wasPinned && !pinned.includes(preview.id)) {
      pinned = resolved.pinned.filter((x) => x === preview.id || pinned.includes(x));
      ghost = preview.id;
    } else if (!wasPinned && !unpinned.includes(preview.id)) {
      unpinned = resolved.unpinned.filter((x) => x === preview.id || unpinned.includes(x));
      ghost = preview.id;
    }
    return { pinned, unpinned, ghost };
  }, [preview, resolved, items]);

  // The rail draws its buttons from this.
  useEffect(() => {
    publishCatalog(items);
  }, [items]);

  // …and marks the button for what's on screen here: the tab's own item, or
  // Apps when the Apps tab is open (whatever is showing inside it).
  const onScreen = activeTab === APPS_ID ? APPS_ID : idForTabKey(activeTab);
  useEffect(() => {
    publishActive(onScreen);
  }, [onScreen]);
  useEffect(() => () => publishActive(null), []);

  const update = useCallback(
    (fn) => {
      const next = fn(resolved.layout);
      setStored(next);
      saveLayout(next);
    },
    [resolved],
  );

  const isPinned = useCallback((id) => LOCKED.includes(id) || resolved.pinned.includes(id), [resolved]);

  /** Open an item wherever it lives: its own tab, or inside Apps. */
  const open = useCallback(
    (id) => {
      if (!id) return;
      if (id === APPS_ID) {
        setActiveTab(APPS_ID);
        return;
      }
      if (!byId[id]) return;
      if (isPinned(id)) {
        setActiveTab(tabKeyFor(id));
      } else {
        setAppsSelection(id);
        setActiveTab(APPS_ID);
      }
    },
    [byId, isPinned, setActiveTab],
  );

  // Keep a real selection inside Apps: the first unpinned item until one is chosen.
  const selection = resolved.unpinned.includes(appsSelection) ? appsSelection : resolved.unpinned[0] || null;

  // The tab on screen was just unpinned (or its app went away): follow it into Apps.
  const activeId = idForTabKey(activeTab);
  useEffect(() => {
    if (!loaded || activeTab === APPS_ID || !byId[activeId]) return;
    if (!isPinned(activeId)) {
      setAppsSelection(activeId);
      setActiveTab(APPS_ID);
    }
  }, [loaded, activeTab, activeId, byId, isPinned, setActiveTab]);

  // Whichever vendor app is on screen is the store's active app — the host
  // positions that one's frame over the visible viewport.
  const shownVendor = isVendorItem(activeId) ? activeId : activeTab === APPS_ID && isVendorItem(selection) ? selection : null;
  useEffect(() => {
    const appId = vendorAppIdOf(shownVendor);
    if (appId && store.activeAppId !== appId) store.setActiveApp(appId);
  }, [shownVendor, store]);

  const isShown = useCallback((id) => activeTab === tabKeyFor(id) || (activeTab === APPS_ID && selection === id), [activeTab, selection]);

  return {
    loaded,
    items,
    byId,
    // What to draw (a drag's live preview included); `savedPinnedIds` is the layout as saved.
    pinned: view.pinned.map((id) => byId[id]),
    unpinned: view.unpinned.map((id) => byId[id]),
    ghost: view.ghost,
    savedPinnedIds: resolved.pinned,
    /** The open chart's patient name, for "not linked to … yet". */
    patientName: store.patient?.name || null,
    /** Every available item in order — what Customize lists. */
    ordered: resolved.order.filter((id) => byId[id]).map((id) => byId[id]),
    selection,
    select: setAppsSelection,
    isPinned,
    isShown,
    open,
    setPinned: (id, pinned) => update((layout) => setPinnedIn(layout, id, pinned)),
    /** Drop `id` on `targetId`; `after` when it landed on the target's far half. */
    moveRelative: (id, targetId, after) => {
      if (id === targetId) return;
      update((layout) => {
        const rest = layout.order.filter((x) => x !== id);
        const at = rest.indexOf(targetId);
        return moveItem(layout, id, after ? rest[at + 1] ?? null : targetId);
      });
    },
    /** Drop `id` next to `targetId` and pin or unpin it on the way — dragging between the tabs and the tray. */
    place: (id, opts) => update((layout) => placeIn(layout, id, opts, new Set(items.map((i) => i.id)))),
    preview,
    /** Show where a dragged item would land; same arguments as place() plus its id. */
    setPreview: setPreviewBoth,
    /** The drop: save the previewed spot. */
    commitPreview: () => {
      const p = previewRef.current;
      if (p) update((layout) => placeIn(layout, p.id, p, new Set(items.map((i) => i.id))));
      setPreviewBoth(null);
    },
    /** The drag ended, dropped or not: anything still only previewed goes back. */
    endDrag: () => {
      dragState.end();
      setDragging(null);
      setPreviewBoth(null);
    },
    nudge: (id, delta, within) => update((layout) => nudgeItem(layout, id, delta, within)),
    reset: () => update(() => ({ order: [], pinned: [] })),
    editing,
    /** Edit layout opens on the Apps tab, so the tray is on screen to drag into. */
    setEditing: (on) => {
      setEditingState(on);
      setMenu(null);
      if (on) setActiveTab(APPS_ID);
    },
    menu,
    showMenu: (event, id) => {
      event.preventDefault();
      event.stopPropagation();
      setMenu({ id, x: event.clientX, y: event.clientY });
    },
    closeMenu: () => setMenu(null),
    compact,
    setCompact,
    dragging,
    setDragging,
  };
}
